import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import axios from "axios";
import { NewVolunteersGallery, UploadProgressHandler, VolunteersGalleryService } from "../../services/volunteersGalleryService";
import { EventService } from "../../services/eventService";
import { VolunteersGalleryValidator } from "../../validators/volunteersGalleryValidator";
import { getErrorMessage } from "../../utils/errorUtils";
import { checkGalleryFile, splitIntoUploadBatches } from "../../utils/galleryMedia";
import { ApiResponse } from "../VolunteersHighlightContext/constants";
import {
	GalleryEventOption,
	getDefaultVolunteersGalleryFormData,
	getVolunteersGalleryEditPath,
	UpdateVolunteersGalleryItemRequest,
	VOLUNTEERS_GALLERY_ERROR_MESSAGES,
	VOLUNTEERS_GALLERY_LIST_PATH,
	VolunteersGalleryContextType,
	volunteersGalleryContextTypeLabels,
	VolunteersGalleryDetail,
	VolunteersGalleryFormData,
	VolunteersGalleryListItem,
	VolunteersGalleryTexts,
	toGalleryErrorMessage,
	UNEXPECTED_GALLERY_ERROR,
} from "./constants";

export type VolunteersGalleryOperationStatus =
	| { type: "idle" }
	| { type: "loading"; operation: "fetch" | "add" | "update" | "delete" | "upload" | "item" };

export interface VolunteersGalleryEditFormData {
	isActive: boolean;
}

interface VolunteersGalleryContextState {
	// Core Domain Data
	volunteersGalleryList: VolunteersGalleryListItem[];
	galleryEvents: GalleryEventOption[];
	volunteersGalleryDetail: VolunteersGalleryDetail | null;
	volunteersGalleryForm: VolunteersGalleryFormData;
	volunteersGalleryEditForm: VolunteersGalleryEditFormData | null;
	operationStatus: VolunteersGalleryOperationStatus;
	// 0-100 while files are uploaded, otherwise null.
	uploadProgress: number | null;

	// Setters
	setVolunteersGalleryForm: React.Dispatch<React.SetStateAction<VolunteersGalleryFormData>>;
	setVolunteersGalleryEditForm: React.Dispatch<React.SetStateAction<VolunteersGalleryEditFormData | null>>;
	clearVolunteersGalleryFormData: () => void;
	clearVolunteersGalleryList: () => void;

	// Actions
	getVolunteersGalleryList: () => Promise<void>;
	getVolunteersGalleryDetails: (galleryId: string) => Promise<void>;
	addVolunteersGallery: () => Promise<void>;
	updateVolunteersGallery: () => Promise<void>;
	removeVolunteersGallery: (galleryId: string) => Promise<void>;
	// Resolves to the files that could not be uploaded (none when every file was uploaded).
	uploadVolunteersGalleryItems: (files: File[]) => Promise<File[]>;
	updateVolunteersGalleryItem: (request: UpdateVolunteersGalleryItemRequest, successMessage: string) => Promise<void>;
	removeVolunteersGalleryItem: (itemId: string) => Promise<void>;
	getGalleryName: (gallery: Pick<VolunteersGalleryListItem, "contextType" | "contextId">) => string;
}

interface VolunteersGalleryProviderProps {
	children: React.ReactNode;
}

const VolunteersGalleryContext = createContext<VolunteersGalleryContextState | undefined>(undefined);

export const useVolunteersGalleryContext = () => {
	const context = useContext(VolunteersGalleryContext);
	if (!context) {
		throw new Error("useVolunteersGalleryContext must be used within a VolunteersGalleryContextProvider");
	}
	return context;
};

/* -------------- Helpers -------------- */

const getGalleryResponseError = (response: ApiResponse<unknown>) =>
	toGalleryErrorMessage(typeof response.error === "string" ? response.error : response.error?.message);

const getGalleryRequestError = (error: unknown) => toGalleryErrorMessage(getErrorMessage(error));

const showRequestError = (error: unknown) => {
	console.error(error);
	message.error(getGalleryRequestError(error));
};

const hasStatus = (error: unknown, status: number) => axios.isAxiosError(error) && error.response?.status === status;

const isSameContext = (gallery: Pick<VolunteersGalleryListItem, "contextType" | "contextId">, contextType: VolunteersGalleryContextType, contextId: string | null) =>
	gallery.contextType === contextType && (gallery.contextId ?? "").toLowerCase() === (contextId ?? "").toLowerCase();

const getTotalSize = (files: File[]) => files.reduce((total, file) => total + file.size, 0);

const getTotalCount = (batches: File[][]) => batches.reduce((total, batch) => total + batch.length, 0);

const toPercent = (part: number, total: number) => (total > 0 ? Math.min(99, Math.round((part / total) * 100)) : 0);

/* -------------- Provider -------------- */

export const VolunteersGalleryContextProvider: React.FC<VolunteersGalleryProviderProps> = ({ children }) => {
	const navigate = useNavigate();
	const [volunteersGalleryList, setVolunteersGalleryList] = useState<VolunteersGalleryListItem[]>([]);
	const [galleryEvents, setGalleryEvents] = useState<GalleryEventOption[]>([]);
	const [volunteersGalleryDetail, setVolunteersGalleryDetail] = useState<VolunteersGalleryDetail | null>(null);
	const [volunteersGalleryForm, setVolunteersGalleryForm] = useState<VolunteersGalleryFormData>(getDefaultVolunteersGalleryFormData);
	const [volunteersGalleryEditForm, setVolunteersGalleryEditForm] = useState<VolunteersGalleryEditFormData | null>(null);
	const [operationStatus, setOperationStatus] = useState<VolunteersGalleryOperationStatus>({ type: "idle" });
	const [uploadProgress, setUploadProgress] = useState<number | null>(null);
	// The loading state only disables the buttons after a re-render, so a fast double click could still send a
	// second request; this ref blocks overlapping changes synchronously.
	const isMutatingRef = useRef(false);

	/* -------------- Clear helpers -------------- */

	const clearVolunteersGalleryFormData = useCallback(() => {
		setVolunteersGalleryForm(getDefaultVolunteersGalleryFormData());
		setVolunteersGalleryEditForm(null);
		setVolunteersGalleryDetail(null);
	}, []);

	const clearVolunteersGalleryList = useCallback(() => {
		setVolunteersGalleryList([]);
	}, []);

	/* -------------- Projects -------------- */

	// The project names of the project galleries and the options of the add form; a failure leaves the list empty.
	const loadGalleryEvents = useCallback(async () => {
		try {
			const response = await EventService.getEventList();
			if (response.isSuccessful) {
				setGalleryEvents((response.result?.items ?? []).map(item => ({ eventId: item.eventId, header: item.header, isActive: item.isActive })));
			} else {
				message.error(getGalleryResponseError(response));
			}
		} catch (error) {
			showRequestError(error);
		}
	}, []);

	const getGalleryName = useCallback(
		(gallery: Pick<VolunteersGalleryListItem, "contextType" | "contextId">) => {
			if (gallery.contextType !== VolunteersGalleryContextType.Event) {
				return volunteersGalleryContextTypeLabels[VolunteersGalleryContextType.MainPage];
			}
			const event = galleryEvents.find(item => isSameContext(gallery, VolunteersGalleryContextType.Event, item.eventId));
			return event?.header || VolunteersGalleryTexts.unknownEvent;
		},
		[galleryEvents]
	);

	/* -------------- Actions -------------- */

	const fetchGalleryList = useCallback(async () => {
		try {
			const response = await VolunteersGalleryService.getVolunteersGalleryList();
			if (response.isSuccessful) {
				setVolunteersGalleryList(response.result?.items ?? []);
			} else {
				message.error(getGalleryResponseError(response));
			}
		} catch (error) {
			showRequestError(error);
		}
	}, []);

	const getVolunteersGalleryList = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });
		await Promise.all([fetchGalleryList(), loadGalleryEvents()]);
		setOperationStatus({ type: "idle" });
	}, [fetchGalleryList, loadGalleryEvents]);

	// Loads the gallery again after a change of its items; the unsaved status switch of the form stays as it is.
	const refreshGalleryDetail = useCallback(async (galleryId: string) => {
		try {
			const response = await VolunteersGalleryService.getVolunteersGalleryDetails(galleryId);
			if (response.isSuccessful) {
				setVolunteersGalleryDetail(response.result);
				return response.result;
			}
			message.error(getGalleryResponseError(response));
		} catch (error) {
			showRequestError(error);
			if (hasStatus(error, 404)) {
				navigate(VOLUNTEERS_GALLERY_LIST_PATH);
			}
		}
		return null;
	}, [navigate]);

	const getVolunteersGalleryDetails = useCallback(async (galleryId: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });
		const [detail] = await Promise.all([refreshGalleryDetail(galleryId), loadGalleryEvents()]);
		if (detail) {
			setVolunteersGalleryEditForm({ isActive: detail.isActive });
		}
		setOperationStatus({ type: "idle" });
	}, [refreshGalleryDetail, loadGalleryEvents]);

	// Sends the batches one after the other and reports the progress over all of them. Resolves to how many
	// batches were saved and, when one failed, why (with the HTTP status of a refused request).
	const uploadBatches = useCallback(
		async (batches: File[][], send: (batch: File[], onProgress: UploadProgressHandler) => Promise<ApiResponse<unknown>>) => {
			const totalBytes = getTotalSize(batches.flat());
			const hasFiles = totalBytes > 0;
			let sentBytes = 0;
			// A gallery added without files has nothing to show progress for.
			setUploadProgress(hasFiles ? 0 : null);

			for (let index = 0; index < batches.length; index++) {
				const batchBytes = getTotalSize(batches[index]);
				const doneBytes = sentBytes;
				const onProgress: UploadProgressHandler = loaded => {
					if (hasFiles) {
						setUploadProgress(toPercent(doneBytes + Math.min(loaded, batchBytes), totalBytes));
					}
				};
				try {
					const response = await send(batches[index], onProgress);
					if (!response.isSuccessful) {
						return { savedBatches: index, error: getGalleryResponseError(response), status: null };
					}
				} catch (error) {
					console.error(error);
					const status = axios.isAxiosError(error) ? error.response?.status ?? null : null;
					return { savedBatches: index, error: getGalleryRequestError(error), status };
				}
				sentBytes += batchBytes;
			}

			setUploadProgress(hasFiles ? 100 : null);
			return { savedBatches: batches.length, error: null, status: null };
		},
		[]
	);

	const findGalleryId = useCallback(async (gallery: NewVolunteersGallery) => {
		const response = await VolunteersGalleryService.getVolunteersGalleryList();
		return response.result?.items?.find(item => isSameContext(item, gallery.contextType, gallery.contextId))?.id ?? null;
	}, []);

	// The gallery was added meanwhile (409): its update page is opened instead, as the backend's guide suggests.
	const openExistingGallery = useCallback(async (gallery: NewVolunteersGallery) => {
		const galleryId = await findGalleryId(gallery).catch(() => null);
		message.info(VOLUNTEERS_GALLERY_ERROR_MESSAGES.VOLUNTEERS_GALLERY_ALREADY_EXISTS);
		navigate(galleryId ? getVolunteersGalleryEditPath(galleryId) : VOLUNTEERS_GALLERY_LIST_PATH);
	}, [findGalleryId, navigate]);

	// The first request adds the gallery with the first batch of files; the API does not return the new gallery's id,
	// so the other batches are added to the gallery found in the list by its place.
	const createGallery = useCallback(async (gallery: NewVolunteersGallery, files: File[]) => {
		const [firstBatch = [], ...otherBatches] = splitIntoUploadBatches(files);
		const firstResult = await uploadBatches([firstBatch], (batch, onProgress) => VolunteersGalleryService.addVolunteersGallery(gallery, batch, onProgress));
		if (firstResult.error) {
			return { created: false, error: firstResult.error, isConflict: firstResult.status === 409 };
		}
		if (otherBatches.length === 0) {
			return { created: true, error: null, galleryId: null };
		}

		const galleryId = await findGalleryId(gallery).catch(() => null);
		if (!galleryId) {
			return { created: true, error: UNEXPECTED_GALLERY_ERROR, galleryId: null, notUploaded: getTotalCount(otherBatches) };
		}
		const result = await uploadBatches(otherBatches, (batch, onProgress) => VolunteersGalleryService.addVolunteersGalleryItems(galleryId, batch, onProgress));
		return { created: true, error: result.error, galleryId, notUploaded: getTotalCount(otherBatches.slice(result.savedBatches)) };
	}, [findGalleryId, uploadBatches]);

	const addVolunteersGallery = useCallback(async () => {
		if (isMutatingRef.current) {
			return;
		}

		const validationError = VolunteersGalleryValidator.validateAddVolunteersGallery(volunteersGalleryForm, volunteersGalleryList);
		if (validationError) {
			message.info(validationError);
			return;
		}
		// The validator has already refused a form without a place.
		const { contextType } = volunteersGalleryForm;
		if (contextType === null) {
			return;
		}

		const gallery: NewVolunteersGallery = {
			contextType,
			contextId: contextType === VolunteersGalleryContextType.Event ? volunteersGalleryForm.contextId : null,
			isActive: volunteersGalleryForm.isActive,
		};

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "add" });

		try {
			const result = await createGallery(gallery, volunteersGalleryForm.files);
			if (!result.created) {
				// Another admin added a gallery for the same place meanwhile.
				if (result.isConflict) {
					await openExistingGallery(gallery);
				} else {
					message.error(result.error);
				}
				return;
			}

			setVolunteersGalleryForm(getDefaultVolunteersGalleryFormData());
			if (result.error) {
				message.error(`Galeri eklendi, ancak ${result.notUploaded} dosya yüklenemedi: ${result.error} Kalan dosyaları galeriyi güncelleyerek yükleyebilirsiniz.`);
				navigate(result.galleryId ? getVolunteersGalleryEditPath(result.galleryId) : VOLUNTEERS_GALLERY_LIST_PATH);
				return;
			}
			message.success("Galeri başarıyla eklendi.");
			navigate(VOLUNTEERS_GALLERY_LIST_PATH);
		} catch (error) {
			showRequestError(error);
		} finally {
			isMutatingRef.current = false;
			setUploadProgress(null);
			setOperationStatus({ type: "idle" });
		}
	}, [volunteersGalleryForm, volunteersGalleryList, createGallery, openExistingGallery, navigate]);

	const updateVolunteersGallery = useCallback(async () => {
		if (isMutatingRef.current) {
			return;
		}
		if (!volunteersGalleryEditForm || !volunteersGalleryDetail) {
			message.error(VolunteersGalleryTexts.notFound);
			return;
		}
		if (volunteersGalleryEditForm.isActive === volunteersGalleryDetail.isActive) {
			message.info(VolunteersGalleryTexts.noChange);
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "update" });

		try {
			const response = await VolunteersGalleryService.updateVolunteersGallery({
				galleryId: volunteersGalleryDetail.id,
				isActive: volunteersGalleryEditForm.isActive,
			});

			if (response.isSuccessful) {
				message.success("Galeri başarıyla güncellendi.");
				clearVolunteersGalleryFormData();
				navigate(VOLUNTEERS_GALLERY_LIST_PATH);
			} else {
				message.error(getGalleryResponseError(response));
			}
		} catch (error) {
			showRequestError(error);
			if (hasStatus(error, 404)) {
				navigate(VOLUNTEERS_GALLERY_LIST_PATH);
			}
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}
	}, [volunteersGalleryEditForm, volunteersGalleryDetail, clearVolunteersGalleryFormData, navigate]);

	const removeVolunteersGallery = useCallback(async (galleryId: string) => {
		if (isMutatingRef.current) {
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await VolunteersGalleryService.removeVolunteersGallery(galleryId);
			if (response.isSuccessful) {
				message.success("Galeri başarıyla silindi.");
			} else {
				message.error(getGalleryResponseError(response));
			}
		} catch (error) {
			showRequestError(error);
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}

		// A gallery deleted elsewhere meanwhile, or a failed delete, still leaves the list up to date.
		await getVolunteersGalleryList();
	}, [getVolunteersGalleryList]);

	const uploadVolunteersGalleryItems = useCallback(async (files: File[]) => {
		if (isMutatingRef.current || !volunteersGalleryDetail) {
			return files;
		}
		if (files.length === 0) {
			message.info(VOLUNTEERS_GALLERY_ERROR_MESSAGES.VOLUNTEERS_GALLERY_ITEM_IMAGE_REQUIRED);
			return files;
		}

		const galleryId = volunteersGalleryDetail.id;
		const batches = splitIntoUploadBatches(files);
		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "upload" });

		try {
			const result = await uploadBatches(batches, (batch, onProgress) => VolunteersGalleryService.addVolunteersGalleryItems(galleryId, batch, onProgress));
			const notUploaded = batches.slice(result.savedBatches).flat();
			if (result.error) {
				const uploaded = files.length - notUploaded.length;
				message.error(uploaded > 0 ? `${uploaded} dosya yüklendi, ${notUploaded.length} dosya yüklenemedi: ${result.error}` : result.error);
			} else {
				message.success(`${files.length} dosya başarıyla yüklendi.`);
			}
			await refreshGalleryDetail(galleryId);
			return notUploaded;
		} finally {
			isMutatingRef.current = false;
			setUploadProgress(null);
			setOperationStatus({ type: "idle" });
		}
	}, [volunteersGalleryDetail, uploadBatches, refreshGalleryDetail]);

	const updateVolunteersGalleryItem = useCallback(async (request: UpdateVolunteersGalleryItemRequest, successMessage: string) => {
		if (isMutatingRef.current || !volunteersGalleryDetail) {
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "item" });

		try {
			let itemRequest = request;
			if (request.image) {
				const check = await checkGalleryFile(request.image);
				if (check.error) {
					message.info(`${request.image.name}: ${check.error}`);
					return;
				}
				itemRequest = { ...request, image: check.file };
			}

			const response = await VolunteersGalleryService.updateVolunteersGalleryItem(itemRequest);
			if (response.isSuccessful) {
				message.success(successMessage);
			} else {
				message.error(getGalleryResponseError(response));
			}
		} catch (error) {
			showRequestError(error);
		} finally {
			// An item deleted elsewhere meanwhile, or a failed change, still leaves the table up to date.
			await refreshGalleryDetail(volunteersGalleryDetail.id);
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}
	}, [volunteersGalleryDetail, refreshGalleryDetail]);

	const removeVolunteersGalleryItem = useCallback(async (itemId: string) => {
		if (isMutatingRef.current || !volunteersGalleryDetail) {
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "item" });

		try {
			const response = await VolunteersGalleryService.removeVolunteersGalleryItem(itemId);
			if (response.isSuccessful) {
				message.success("Galeri öğesi başarıyla silindi.");
			} else {
				message.error(getGalleryResponseError(response));
			}
		} catch (error) {
			showRequestError(error);
		} finally {
			await refreshGalleryDetail(volunteersGalleryDetail.id);
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}
	}, [volunteersGalleryDetail, refreshGalleryDetail]);

	const contextValue = useMemo<VolunteersGalleryContextState>(
		() => ({
			volunteersGalleryList,
			galleryEvents,
			volunteersGalleryDetail,
			volunteersGalleryForm,
			volunteersGalleryEditForm,
			operationStatus,
			uploadProgress,
			setVolunteersGalleryForm,
			setVolunteersGalleryEditForm,
			clearVolunteersGalleryFormData,
			clearVolunteersGalleryList,
			getVolunteersGalleryList,
			getVolunteersGalleryDetails,
			addVolunteersGallery,
			updateVolunteersGallery,
			removeVolunteersGallery,
			uploadVolunteersGalleryItems,
			updateVolunteersGalleryItem,
			removeVolunteersGalleryItem,
			getGalleryName,
		}),
		[
			volunteersGalleryList,
			galleryEvents,
			volunteersGalleryDetail,
			volunteersGalleryForm,
			volunteersGalleryEditForm,
			operationStatus,
			uploadProgress,
			clearVolunteersGalleryFormData,
			clearVolunteersGalleryList,
			getVolunteersGalleryList,
			getVolunteersGalleryDetails,
			addVolunteersGallery,
			updateVolunteersGallery,
			removeVolunteersGallery,
			uploadVolunteersGalleryItems,
			updateVolunteersGalleryItem,
			removeVolunteersGalleryItem,
			getGalleryName,
		]
	);

	return <VolunteersGalleryContext.Provider value={contextValue}>{children}</VolunteersGalleryContext.Provider>;
};
